/* eslint-disable compat/compat */
/**
 * @fileOverview Callback-Modal initializations.
 * @author Enzo de Villarreal - Selectra Front End Team
 */

// Dependencies
import displayDynamicCallBlocks from 'design-system/src/js/03-organisms/call-block';
import validatePhone from '../helpers/validate-phone';
import validateName from '../helpers/validate-name';
import validateTime from '../helpers/validate-time';
import validatePrivacy from '../helpers/validate-privacy';
import validateEmail from '../helpers/validate-email';
import submitCallbackForm from './callback-form-submission';

export default function initCallbackModals(callBlockPath) {

  const callbackModalTriggers = document.querySelectorAll(
    '[data-toggle="modal"][data-target="callback-modal"]'
  ),
    callbackModalLanding = document.querySelector('.callback-modal--landing');

  if (callbackModalTriggers.length > 0) {
    const
      callbackModal = createCallbackModal();

    callbackModal.addEventListener('modalfetched', event => {
      const
        fetchedModal = event.target,
        fetchedCloseButton = fetchedModal.querySelector('.btn-close'),
        modalURL = event.detail.fetchedURL,
        openSelectors = '#callback-modal [data-callcenter].callback-modal__callcenter--open',
        closedSelectors = '#callback-modal [data-callcenter].callback-modal__callcenter--close';


      if (fetchedCloseButton) {
        fetchedCloseButton.addEventListener('click', event => {
          window.selectra.ds.modals.close(event);
        });
      }

      invokeInitFetchedForm(fetchedModal, modalURL, openSelectors, closedSelectors, callBlockPath);
    });
  }

  if (callbackModalLanding) {
    const
      modalURL = callbackModalLanding.dataset.modalLandingUrl,
      openSelectors = '.callback-modal--landing [data-callcenter].callback-modal__callcenter--open',
      closedSelectors = '.callback-modal--landing [data-callcenter].callback-modal__callcenter--close';

    invokeInitFetchedForm(callbackModalLanding, modalURL, openSelectors, closedSelectors, callBlockPath);
  }
}

// Invoke initialization of callback modal form - common for landing and modal.
function invokeInitFetchedForm(fetchedModal, modalURL, openSelectors, closedSelectors, callBlockPath) {
  // If we are fetching it at backend, then we will directly use status from backend and init the form.
  var dataCallCenterStatus = fetchedModal.querySelectorAll('[data-callcenter-status].call-block--active');
  if (dataCallCenterStatus.length > 0) {
    dataCallCenterStatus = dataCallCenterStatus[0].getAttribute('data-callcenter-status');
    initFetchedForm(fetchedModal, modalURL, dataCallCenterStatus);
  }
  else {
    // Else
    displayDynamicCallBlocks({
      openSelectors: openSelectors,
      closedSelectors: closedSelectors,
      dataPath: callBlockPath
    }).then((ccStatus) => {
      let status;
      for (let key in ccStatus) {
        status = ccStatus[key].state;
      }
      initFetchedForm(fetchedModal, modalURL, status);
    }).catch(error => {
      console.error('Dynamic Call-blocks:', error);
    });
  }
}


// Creates an empty modal on the DOM to inject the fetched content
function createCallbackModal() {
  const callbackModal = document.getElementById('callback-modal');

  if (callbackModal) {
    return callbackModal;
  } else {
    let
      modalHolder = document.createElement('div'),
      modalDialog = document.createElement('div'),
      modal = document.createElement('div'),
      main = document.querySelector('main'),
      body = document.querySelector('body'),
      insertionPoint = main ? main : body;

    modalHolder.classList.add('modal__holder')
    modalDialog.classList.add('modal__dialog')
    modal.classList.add('modal', 'modal--callback')
    modal.setAttribute('id', 'callback-modal')
    modal.setAttribute('role', 'dialog')
    modal.setAttribute('aria-modal', 'true')

    modalDialog.appendChild(modal)
    modalHolder.appendChild(modalDialog)
    body.appendChild(modalHolder)
    modalHolder.addEventListener('click', event => {
      window.selectra.ds.modals._clickToClose(event);
    })

    return document.getElementById('callback-modal')
  }
}

// Initializes the validations for the corresponding fields  (open/close)
function initFetchedForm(modal, modalURL, status) {
  const
    submit = modal.querySelector('#callback-modal__submit, #callback-modal__submit--landing'),
    fetchedDropdowns = modal.querySelectorAll('.dropdown, .dropdown-landing'),
    dropdownOptions = {
      sortList: false,
    },
    callback_form = modal.querySelectorAll('.modal__form[data-form-id]'),
    timestamp = modal.querySelector('#callback-modal__timestamp, #callback-modal__timestamp--landing'),
    name = modal.querySelector('#callback-modal__name, #callback-modal__name--landing'),
    phone = modal.querySelector('#callback-modal__phone, #callback-modal__phone--landing'),
    email = modal.querySelector('#callback-modal__email, #callback-modal__email--landing'),
    day = modal.querySelector('select[name="dropdown-01"]'),
    hour = modal.querySelector('select[name="dropdown-02"]'),
    privacy = modal.querySelector('input[name="callback-modal__privacy"], input[name="callback-modal__privacy--landing"]'),
    country = document.querySelector('meta[property="og:country_name"]').content;

  if (fetchedDropdowns.length > 0) {
    fetchedDropdowns.forEach(dropdown => {
      window.selectra.ds.dropdownManager.createDropdown(dropdown, dropdownOptions)
    });
  }

  if (name) {
    name.addEventListener('blur', event => {
      validateName(event.target)
    });

    name.addEventListener('focus', event => {
      const formGroup = event.target.closest('.form-group');
      formGroup.classList.remove('form-group--has-danger');
      formGroup.classList.remove('form-group--has-success');
    });
  }

  if (phone) {
    phone.addEventListener('blur', event => {
      validatePhone(event.target, country)
    });

    phone.addEventListener('focus', event => {
      const formGroup = event.target.closest('.form-group');
      formGroup.classList.remove('form-group--has-danger');
      formGroup.classList.remove('form-group--has-success');
    });
  }

  if (email) {
    email.addEventListener('blur', event => {
      validateEmail(event.target)
    });

    email.addEventListener('focus', event => {
      const formGroup = event.target.closest('.form-group');
      formGroup.classList.remove('form-group--has-danger');
      formGroup.classList.remove('form-group--has-success');
    });
  }

  if (day && hour) {
    day.addEventListener('change', event => {
      validateTime(event.target)
    });

    hour.addEventListener('change', event => {
      validateTime(event.target)
    });
  }

  if (privacy) {
    privacy.addEventListener('change', event => {
      validatePrivacy(event.target)
    })
  }

  submit.addEventListener('click', event => {
    event.preventDefault();
    const fieldsToValidate = [];
    let formIsValid = true;

    if (name) {
      fieldsToValidate.push(name)
    }

    fieldsToValidate.push(phone)

    if (day && hour) {
      fieldsToValidate.push(day)
      fieldsToValidate.push(hour)
    }

    if (privacy) {
      fieldsToValidate.push(privacy)
    }

    if (email) {
      fieldsToValidate.push(email)
    }

    fieldsToValidate.forEach(field => {
      switch (field.id) {
        case 'callback-modal__name':
        case 'callback-modal__name--landing':
          validateName(field)
          break;
        case 'callback-modal__privacy':
        case 'callback-modal__privacy--landing':
          validatePrivacy(field)
          break;
        case 'callback-modal__phone':
        case 'callback-modal__phone--landing':
          validatePhone(field, country)
          break;
        case 'callback-modal__day':
          validateTime(field)
          break;
        case 'callback-modal__hour':
          validateTime(field)
          break;
        case 'callback-modal__email':
        case 'callback-modal__email--landing':
          validateEmail(field)
          break;
      }
    })

    formIsValid = fieldsToValidate.every(field => {
      switch (field.id) {
        case 'callback-modal__name':
        case 'callback-modal__name--landing':
          return validateName(field)
        case 'callback-modal__privacy':
        case 'callback-modal__privacy--landing':
          return validatePrivacy(field)
        case 'callback-modal__phone':
        case 'callback-modal__phone--landing':
          return validatePhone(field, country)
        case 'callback-modal__day':
          return validateTime(field)
        case 'callback-modal__hour':
          return validateTime(field)
        case 'callback-modal__email':
        case 'callback-modal__email--landing':
          return validateEmail(field)
      }
    })

    if (formIsValid) {
      let submitOptions = {};

      if (name) {
        submitOptions.name = name.value;
      }

      if (day && hour) {
        submitOptions.day = day.value;
        submitOptions.hour = hour.value;
      }

      submitOptions.token = callback_form.item(0).dataset.formId;
      submitOptions.timestamp = timestamp.value;
      submitOptions.phone = phone.value;
      submitOptions.country = country;
      submitOptions.status = status;
      submitOptions.modalURL = modalURL;
      if (email) {
        submitOptions.email = email.value;
      }

      console.log(submitOptions)

      submitCallbackForm(submitOptions, event.target)
    }
  })
}
